import { UserEndpoint } from '@api/endpoints';
import { QueryUtil } from '../Query';
import { Client } from './Client';

export class UserApi {
  static create (
    request: UserEndpoint.Create.Request,
  ): Promise<UserEndpoint.Create.Response> {
    return Client(`admin/users`, { body: request, method: 'POST' });
  }

  static delete (id: string): Promise<void> {
    return Client(`users/${id}`, { method: 'DELETE' });
  }

  static get (id: string): Promise<UserEndpoint.Get.Response> {
    return Client(`users/${id}`);
  }

  static getFarmersAssociatedToSalesperson (
    query?: UserEndpoint.AssociatedFarmers.Query,
  ): Promise<UserEndpoint.AssociatedFarmers.Response> {
    return Client(`salespersons/farmers?${QueryUtil.stringify(query ?? {})}`);
  }

  static getAssociatedSellers (id: string): Promise<UserEndpoint.ListAssociatedSellers.Response> {
    return Client(`users/${id}/associated-sellers`);
  }

  static getSubwalletBalances (
    id: string,
  ): Promise<UserEndpoint.ListSubwalletBalances.Response> {
    return Client(`users/${id}/subwallet-balances`);
  }

  static getLinkedAccounts (id: string): Promise<UserEndpoint.LinkedAccounts.Response> {
    return Client(`users/${id}/linked-accounts`);
  }

  static async getLocationsForUser (id: string): Promise<UserEndpoint.ListLocations.Response> {
    return Client(`users/${id}/locations`);
  }

  static list (
    filters?: UserEndpoint.List.Query,
    usePost?: boolean,
  ): Promise<UserEndpoint.List.Response> {
    if (usePost) {
      return Client('users/list', { body: filters, method: 'POST' });
    }
    if (filters && filters.retailerId === null) {
      filters.retailerId = 'null';
    }
    const query = QueryUtil.stringify(filters ?? {});
    return Client(`users?${query}`);
  }

  static async listPointsForProgramPeriod (
    userId: string,
    programId: string,
    accessToken?: string,
    forFarmerOnly?: boolean,
  ): Promise<UserEndpoint.ListPointsForProgramPeriod.Response> {
    const params = new URLSearchParams();
    if (accessToken) params.set('accessToken', accessToken);
    if (forFarmerOnly) params.set('forFarmerOnly', 'true');
    const query = params.toString();
    const url = `users/${userId}/points/${programId}${query ? `?${query}` : ''}`;
    return Client(url);
  }

  static me (): Promise<UserEndpoint.Me.Response> {
    return Client('users/me');
  }

  static resendVerifyEmail (id: string): Promise<void> {
    return Client(`users/${id}/resend-verify-email`, {
      method: 'POST',
    });
  }

  static switchAccount (
    body: UserEndpoint.SwitchAccount.Request,
  ): Promise<UserEndpoint.SwitchAccount.Response> {
    return Client(`switch-account`, { body });
  }

  static getSwitchAccountTicket (): Promise<UserEndpoint.SwitchAccountTicket.Response> {
    return Client(`switch-account/ticket`);
  }

  static update (
    userId: string,
    data: UserEndpoint.Update.Request,
  ): Promise<UserEndpoint.Update.Response> {
    return Client(`users/${userId}`, {
      method: 'PATCH',
      body: { id: userId, ...data },
    });
  }

  static verifyEmail (token: string): Promise<void> {
    const query = QueryUtil.stringify({ token });
    return Client(`verify-email?${query}`, { method: 'POST' });
  }

  static getVerifiedStatus (
    id: string,
  ): Promise<UserEndpoint.VerifiedStatus.Response> {
    return Client(`users/${id}/verified`);
  }

  static getFarmerRewards (farmerId: string): Promise<UserEndpoint.ListFarmerRewards.Response> {
    return Client(`users/${farmerId}/farmer-rewards`);
  }

  static manualPointsEdit (
    userId: string,
    body: UserEndpoint.SubwalletBalance.ManualPointAdjustment,
  ) {
    return Client(`users/${userId}/manual-points-adjustment`, {
      method: 'POST',
      body,
    });
  }
}
